import type { DocumentNode, OperationVariables } from '@apollo/client';
import {
  type ExportPollStates,
  type PolledExport,
  useExportPoll,
} from './useExportPoll';

type CreateExportPollConfig<TData, TState extends string = string> = {
  query: DocumentNode;
  selectExport: (
    data: TData | undefined,
  ) => PolledExport<TState> | null | undefined;
  states: ExportPollStates<TState>;
};

export type ExportPollHookOptions<TVariables extends OperationVariables> = {
  variables: TVariables;
  enabled: boolean;
  onComplete: (fileUrl: string) => void;
  onFail: () => void;
};

/**
 * Binds `useExportPoll` to one export type's query/selector/states, so
 * consumers don't each redefine the same config. The returned hook must be
 * called unconditionally (like any hook) and driven with `enabled` — mirrors
 * `useQuery`'s `skip` rather than mounting/unmounting a wrapper component.
 */
export const createExportPoll = <
  TData,
  TVariables extends OperationVariables,
  TState extends string = string,
>(
  config: CreateExportPollConfig<TData, TState>,
) => {
  return (options: ExportPollHookOptions<TVariables>) =>
    useExportPoll<TData, TVariables, TState>({ ...config, ...options });
};
